﻿import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import TicketTypeBadge from './TicketTypeBadge'
import ColumnSettings from '../Shared/ColumnSettings'
import UserAvatar from '../Shared/UserAvatar'
import SortableTh from '../Shared/SortableTh'
import useColumnLayout from '../../hooks/useColumnLayout'
import useTicketCatalogs from '../../hooks/useTicketCatalogs'
import usePersistedState from '../../hooks/usePersistedState'
import {
  DEFAULT_TICKET_SORT,
  TICKET_TABLE_COLUMNS,
  isSortable,
  nextSort,
  sortTickets,
} from '../../utils/ticketTable'
import { getPriorityMeta } from '../../utils/priorityMatrix'
import { SLA_META, getSlaInfo } from '../../utils/sla'
import { employeeName } from '../../utils/employee'
import '../../styles/data-table.css'

/** Nombres de estado usados si el catálogo `Tickets.status` no está disponible. */
const FALLBACK_STATUS = { 1: 'Pendiente', 2: 'En proceso', 3: 'Resuelto' }
const STATUS_FALLBACK_COLOR = 'var(--text-2)'

/** Formatea una fecha ISO como `dd/mm/aa` (o `—` si no hay). */
const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('es-CR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
    : '—'

/** Persona con avatar y nombre (autor o responsable). */
const PersonCell = ({ employee, fallback }) => (
  <span className="tt-person">
    <UserAvatar employee={employee} size={20} />
    <span className="tt-ellipsis">{employeeName(employee) || fallback}</span>
  </span>
)

/** Etiqueta coloreada compacta (estado, prioridad, SLA). El tono lo usa
 *  .gm-tono, que decide cómo pintarlo según el tema. */
const ColorBadge = ({ color, icon, children }) => (
  <span className="tt-badge gm-tono" style={{ '--tono': color }}>
    {icon && <i className={`fa-solid ${icon}`} />}
    {children}
  </span>
)

/**
 * Cómo se pinta cada columna declarada en `TICKET_TABLE_COLUMNS`.
 * Para agregar una columna: declararla allí y registrar aquí su celda (y, si
 * se puede ordenar, su valor en `SORT_ACCESSORS` de `utils/ticketTable`).
 * @type {Record<string, (t: Object, ctx: Object) => React.ReactNode>}
 */
const CELL_RENDERERS = {
  id: (t) => <span className="tt-id">#{t.id}</span>,
  title: (t) => (
    <Link
      to={`/ticket/${t.id}`}
      className="tt-title"
      title={t.title}
      onClick={(e) => e.stopPropagation()}
    >
      {t.title}
    </Link>
  ),
  type: (t) => <TicketTypeBadge type={t.type} sm />,
  origin: (t) => <span className="tt-muted">{t.systemOrigin || '—'}</span>,
  priority: (t) => {
    const meta = getPriorityMeta(t.priorityId)
    return <ColorBadge color={meta.color}>{meta.label}</ColorBadge>
  },
  status: (t, { statusById }) => {
    const status = statusById[t.statusId]
    return (
      <ColorBadge color={status?.color || STATUS_FALLBACK_COLOR}>
        {status?.name || FALLBACK_STATUS[t.statusId] || `#${t.statusId}`}
      </ColorBadge>
    )
  },
  queue: (t, { queueById }) => {
    const queue = queueById[t.queueId]
    return queue ? (
      <span className="tt-person">
        <span className="tt-dot" style={{ background: queue.color }} />
        <span className="tt-ellipsis">{queue.name}</span>
      </span>
    ) : (
      <span className="tt-muted">—</span>
    )
  },
  responsible: (t, { employeeById }) =>
    t.responsibleId ? (
      <PersonCell
        employee={employeeById[t.responsibleId]}
        fallback={`#${t.responsibleId}`}
      />
    ) : (
      <span className="tt-muted fst-italic">Sin asignar</span>
    ),
  author: (t, { employeeByAuthId }) => (
    <PersonCell employee={employeeByAuthId[t.userId]} fallback="—" />
  ),
  sla: (t) => {
    const meta = SLA_META[getSlaInfo(t).overall]
    return (
      <ColorBadge color={meta.color} icon={meta.icon}>
        {meta.label}
      </ColorBadge>
    )
  },
  createdAt: (t) => <span className="tt-muted">{formatDate(t.createdAt)}</span>,
  dueAt: (t) => <span className="tt-muted">{formatDate(t.dueAt)}</span>,
}

/**
 * Tabla compacta de tickets: columnas configurables (cuáles y en qué orden,
 * desde el botón de configuración) y orden por columna al pulsar el
 * encabezado. Ambas preferencias se guardan por tabla (`storageKey`).
 *
 * La tabla sólo muestra: la búsqueda y los filtros los hace la página.
 * Ocupa el alto de su contenedor y scrollea por dentro con el encabezado fijo.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} [props.items=[]] - Tickets ya filtrados.
 * @param {number} [props.totalItems=0] - Total antes de filtrar (para el contador).
 * @param {string} [props.storageKey='tickets.table.columns'] - Clave de las
 *   preferencias de esta tabla.
 * @returns {JSX.Element}
 */
const TableTickets = ({
  items = [],
  totalItems = 0,
  storageKey = 'tickets.table.columns',
}) => {
  const navigate = useNavigate()
  const { statusById, employeeById, employeeByAuthId, queueById } =
    useTicketCatalogs()
  const { layout, visibleColumns, toggleColumn, moveColumn, resetLayout } =
    useColumnLayout(storageKey, TICKET_TABLE_COLUMNS)
  const [sort, setSort] = usePersistedState(
    `${storageKey}:sort`,
    DEFAULT_TICKET_SORT
  )
  const [showSettings, setShowSettings] = useState(false)

  const rows = useMemo(
    () =>
      sortTickets(items, sort, { employeeById, employeeByAuthId, queueById }),
    [items, sort, employeeById, employeeByAuthId, queueById]
  )

  const cellContext = {
    statusById,
    employeeById,
    employeeByAuthId,
    queueById,
  }
  const sortLabel = TICKET_TABLE_COLUMNS.find((c) => c.key === sort?.key)?.label

  return (
    <div className="tt">
      {/* CABECERA: conteo, orden actual y configuración */}
      <div className="tt-bar">
        <span className="tt-muted">
          <strong className="tt-strong">{rows.length}</strong>
          {rows.length !== totalItems && ` de ${totalItems}`} registros
          {sortLabel && (
            <>
              {' · '}orden: {sortLabel}{' '}
              <i
                className={`fa-solid fa-arrow-${sort.dir === 'asc' ? 'up' : 'down'}-short`}
              />
            </>
          )}
        </span>
        <button
          type="button"
          className={`tt-settings ${showSettings ? 'is-open' : ''}`}
          onClick={() => setShowSettings((v) => !v)}
          aria-expanded={showSettings}
          title="Configurar columnas"
        >
          <i className="fa-solid fa-gear" />
          <span>Columnas</span>
        </button>
      </div>

      <AnimatePresence initial={false}>
        {showSettings && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ overflow: 'hidden', flexShrink: 0 }}
          >
            <div className="tt-settings-panel">
              <ColumnSettings
                columns={layout}
                onToggle={toggleColumn}
                onMove={moveColumn}
                onReset={() => {
                  resetLayout()
                  setSort(DEFAULT_TICKET_SORT)
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TABLA: scroll propio con encabezado fijo */}
      <div className="tt-scroll">
        <table className="tt-table">
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <SortableTh
                  key={col.key}
                  label={col.label}
                  className={col.className}
                  sortable={isSortable(col.key)}
                  direction={sort?.key === col.key ? sort.dir : null}
                  onSort={() => setSort((s) => nextSort(s, col.key))}
                />
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="tt-empty">
                  No hay tickets que coincidan con la búsqueda
                </td>
              </tr>
            ) : (
              rows.map((t) => (
                <tr
                  key={t.id}
                  className="tt-row"
                  onClick={() => navigate(`/ticket/${t.id}`)}
                >
                  {visibleColumns.map((col) => (
                    <td key={col.key} className={col.className || ''}>
                      {CELL_RENDERERS[col.key]?.(t, cellContext)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TableTickets
